/**
 * Texts a person can start from on Write your text. Bracketed words are for them to fill in; a template
 * never carries a link, so nothing in it points voters somewhere the person didn't choose.
 */
export const MESSAGE_TEMPLATES = [
  {
    key: 'earlyVoting',
    label: 'Early voting',
    body: 'Hi, this is [your name], running for [office]. Early voting is open now, so you can skip the Election Day lines. I’d be honored to have your vote.',
  },
  {
    key: 'electionDay',
    label: 'Election Day',
    body: 'Today is Election Day! I’m [your name], running for [office], and I’m asking for your vote. If you haven’t voted yet, there’s still time to get to the polls.',
  },
  {
    key: 'eventInvite',
    label: 'Event invite',
    body: 'Join [your name], candidate for [office], for a neighborhood meet and greet this Saturday. Bring your questions and your neighbors. Everyone is welcome.',
  },
] as const satisfies readonly { key: string; label: string; body: string }[];

export type MessageTemplate = (typeof MESSAGE_TEMPLATES)[number];

/** The template text last written into the message, so a body still equal to it can be swapped without asking. */
type Written = { template: MessageTemplate; text: string } | null;

/**
 * Picking a template. Typing reveals it a few characters at a time; a body the person wrote themselves is
 * only replaced after they confirm.
 */
export type TemplateTyping =
  | { kind: 'idle'; written: Written }
  | { kind: 'confirming'; template: MessageTemplate; written: Written }
  | { kind: 'typing'; template: MessageTemplate; shown: number };

export type TemplateTypingAction =
  | { type: 'confirm'; template: MessageTemplate }
  | { type: 'keep' }
  | { type: 'type'; template: MessageTemplate }
  | { type: 'insert'; template: MessageTemplate }
  | { type: 'tick' }
  | { type: 'stop' };

export const TYPING_TICK_MS = 26;

/** Characters revealed per tick, so any template finishes in about 1.2 seconds. */
export const charsPerTick = (template: MessageTemplate) => Math.ceil(template.body.length / 45);

export const IDLE: TemplateTyping = { kind: 'idle', written: null };

export const writtenBy = (state: TemplateTyping): Written =>
  state.kind === 'typing' ? { template: state.template, text: state.template.body.slice(0, state.shown) } : state.written;

export function templateTyping(state: TemplateTyping, action: TemplateTypingAction): TemplateTyping {
  switch (action.type) {
    case 'confirm':
      return { kind: 'confirming', template: action.template, written: writtenBy(state) };
    case 'keep':
      return state.kind === 'confirming' ? { kind: 'idle', written: state.written } : state;
    case 'type':
      return { kind: 'typing', template: action.template, shown: 0 };
    case 'insert':
      return { kind: 'idle', written: { template: action.template, text: action.template.body } };
    case 'tick':
      if (state.kind !== 'typing') return state;
      return state.shown >= state.template.body.length
        ? { kind: 'idle', written: writtenBy(state) }
        : { ...state, shown: Math.min(state.template.body.length, state.shown + charsPerTick(state.template)) };
    case 'stop':
      return state.kind === 'typing' ? { kind: 'idle', written: writtenBy(state) } : state;
  }
}

/** Whether picking a template may replace `body` without asking: it is empty, or still exactly what a template wrote. */
export function mayReplace(state: TemplateTyping, body: string): boolean {
  if (body.trim() === '' || state.kind === 'typing') return true;
  return state.written?.text === body;
}

/** The template the message currently shows, untouched, if any. */
export function templateShown(state: TemplateTyping, body: string): MessageTemplate | null {
  if (state.kind === 'typing') return state.template;
  return state.written?.text === body ? state.written.template : null;
}
